import { Box, CircularProgress, LinearProgress, Typography } from "@mui/material";
import { ReactNode } from "react";

interface BusyOverlayProps {
  /** Whether the work is in flight. */
  busy: boolean;
  /** What is happening, in the user's words: "Saving events", "Approving trip". */
  message?: string;
  /** Optional second line for work that is expected to take a while. */
  detail?: string;
  children: ReactNode;
}

/**
 * Covers its own content while something is happening to it, and says what.
 *
 * The closer screen used to swap whole regions for a grey skeleton mid-operation. A skeleton is
 * the right answer for a FIRST load, where there is genuinely nothing to show yet; it is the wrong
 * answer once the screen has content, because it throws away what the user was reading, hides the
 * controls, and says nothing about what is going on. Approving made that plain -- the grid went
 * blank and the next thing the closer saw was a validation error, which read as the button having
 * done nothing at all.
 *
 * So: keep the content on screen, dim it so it is clearly not current, and name the operation.
 * The bar along the top gives the movement that tells a reader the page is alive rather than
 * wedged, and `aria-busy` plus a live region mean a screen reader is told the same thing.
 *
 * Deliberately scoped to the region it wraps rather than the whole page: a save concerns the
 * events grid, and blanking the trip header alongside it would be a lie about what is changing.
 */
const BusyOverlay = ({ busy, message, detail, children }: BusyOverlayProps) => (
  <Box sx={{ position: "relative" }} aria-busy={busy}>
    <Box
      sx={{
        // Not `display: none` -- the content stays laid out so the region keeps its size and the
        // page does not jump when the overlay clears.
        opacity: busy ? 0.35 : 1,
        pointerEvents: busy ? "none" : "auto",
        transition: "opacity 120ms ease",
      }}
      // Anything underneath is stale for as long as this is up, so keep it out of the tab order
      // and off the accessibility tree rather than letting it be read or focused.
      {...(busy ? { inert: "" as any, "aria-hidden": true } : {})}
    >
      {children}
    </Box>

    {busy && (
      <>
        <LinearProgress
          sx={{ position: "absolute", top: 0, left: 0, right: 0, borderRadius: "4px 4px 0 0" }}
        />
        <Box
          role="status"
          aria-live="polite"
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            // Sits in the top third rather than dead centre: on a tall grid the centre can be
            // well below the fold, so the one thing the user is waiting to read ends up off
            // screen. Near the top it is visible whatever the region's height.
            justifyContent: "flex-start",
            paddingTop: "12vh",
            gap: 1.5,
            // Sits above the dimmed content without hiding it: the reader can still see which
            // trip and which rows the operation is about.
            backgroundColor: "rgba(255, 255, 255, 0.55)",
            backdropFilter: "blur(1px)",
            zIndex: 2,
          }}
        >
          <CircularProgress size={34} />
          {message && (
            <Typography variant="button" sx={{ fontWeight: 600, color: "text.primary" }}>
              {message}
            </Typography>
          )}
          {detail && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {detail}
            </Typography>
          )}
        </Box>
      </>
    )}
  </Box>
);

export default BusyOverlay;
